import type { ApiClient } from './client';

/** Тема виджета (ANX-022 итерация B). Хранится в sites.widget_settings;
 *  backend парсит/валидирует и отдаёт типизированный объект. */
export interface WidgetTheme {
  theme_color: string;
  position: 'right' | 'left';
  greeting: string;
}

export interface Site {
  id: string;
  name: string;
  domain: string | null;
  widgetKey: string;
  widgetSettings: WidgetTheme;
  status: 'active' | 'paused' | 'deleted';
  createdAt: string;
  /** ANX-050: admin включил отправку email-транскриптов после закрытия диалога. */
  emailTranscriptsEnabled: boolean;
  /** ANX-051: pro-active trigger — виджет сам открывается через delay сек. */
  proactiveEnabled: boolean;
  proactiveDelaySec: number;
  proactiveMessage: string;
  proactiveMobileEnabled: boolean;
  /** ANX-061: часы работы + offline-режим виджета. */
  officeHoursEnabled: boolean;
  officeTimezone: string;
  officeHours: Record<string, OfficeDay>;
  offlineMessage: string;
  offlineCollectEmail: boolean;
  /** ANX-067: детект установки виджета. null last_seen → не обнаружен. */
  widgetLastSeenAt: string | null;
  widgetSeenDomain: string | null;
  /** ANX-072: pre-chat форма + выбор отдела. */
  prechatEnabled: boolean;
  prechatConfig: PrechatConfig;
  /** ANX-079: follow-up email на брошенный диалог. */
  followUpEmailEnabled: boolean;
  /** ANX-065: подключён ли Telegram-бот к сайту (токен наружу не отдаётся). */
  telegramConnected: boolean;
}

/** ANX-061: интервал рабочего дня, "HH:MM"–"HH:MM". */
export interface OfficeDay {
  start: string;
  end: string;
}

/** ANX-072: одно поле pre-chat формы. */
export interface PrechatField {
  enabled: boolean;
  required: boolean;
}

/** ANX-072: конфиг pre-chat формы (sites.prechat_config). */
export interface PrechatConfig {
  fields: { name: PrechatField; email: PrechatField; phone: PrechatField };
  question: PrechatField;
  department: PrechatField;
  intro: string;
}

function toField(r: Partial<PrechatField> | null | undefined): PrechatField {
  const required = r?.required === true;
  return { enabled: r?.enabled === true || required, required };
}

/** Нормализует сырой prechat_config с дефолтами (всё выключено). */
export function toPrechatConfig(raw: unknown): PrechatConfig {
  const c = (raw && typeof raw === 'object' ? raw : {}) as {
    fields?: { name?: Partial<PrechatField>; email?: Partial<PrechatField>; phone?: Partial<PrechatField> };
    question?: Partial<PrechatField>;
    department?: Partial<PrechatField>;
    intro?: string;
  };
  const f = c.fields ?? {};
  return {
    fields: { name: toField(f.name), email: toField(f.email), phone: toField(f.phone) },
    question: toField(c.question),
    department: toField(c.department),
    intro: typeof c.intro === 'string' ? c.intro : '',
  };
}

interface SiteRaw {
  id: string;
  name: string;
  domain: string | null;
  widget_key: string;
  widget_settings: Partial<WidgetTheme> | null;
  status: string;
  created_at: string;
  email_transcripts_enabled?: boolean;
  proactive_enabled?: boolean;
  proactive_delay_sec?: number;
  proactive_message?: string;
  proactive_mobile_enabled?: boolean;
  office_hours_enabled?: boolean;
  office_timezone?: string;
  office_hours?: Record<string, { start?: string; end?: string }> | null;
  offline_message?: string;
  offline_collect_email?: boolean;
  widget_last_seen_at?: string | null;
  widget_seen_domain?: string | null;
  prechat_enabled?: boolean;
  prechat_config?: unknown;
  follow_up_email_enabled?: boolean;
  telegram_connected?: boolean;
}

/** Нормализует сырое office_hours: оставляет только дни с валидной парой start/end. */
function toOfficeHours(raw: Record<string, { start?: string; end?: string }> | null | undefined): Record<string, OfficeDay> {
  const out: Record<string, OfficeDay> = {};
  if (!raw) return out;
  for (const [k, v] of Object.entries(raw)) {
    if (v && typeof v.start === 'string' && typeof v.end === 'string') {
      out[k] = { start: v.start, end: v.end };
    }
  }
  return out;
}

function toSite(r: SiteRaw): Site {
  const ws = r.widget_settings ?? {};
  return {
    id: r.id,
    name: r.name,
    domain: r.domain,
    widgetKey: r.widget_key,
    widgetSettings: {
      theme_color: ws.theme_color || '#1292EE',
      position: ws.position === 'left' ? 'left' : 'right',
      greeting: ws.greeting ?? '',
    },
    status: r.status as Site['status'],
    createdAt: r.created_at,
    emailTranscriptsEnabled: r.email_transcripts_enabled === true,
    proactiveEnabled: r.proactive_enabled === true,
    proactiveDelaySec: typeof r.proactive_delay_sec === 'number' ? r.proactive_delay_sec : 60,
    proactiveMessage: r.proactive_message ?? '',
    proactiveMobileEnabled: r.proactive_mobile_enabled === true,
    officeHoursEnabled: r.office_hours_enabled === true,
    officeTimezone: r.office_timezone || 'UTC',
    officeHours: toOfficeHours(r.office_hours),
    offlineMessage: r.offline_message ?? '',
    offlineCollectEmail: r.offline_collect_email !== false,
    widgetLastSeenAt: r.widget_last_seen_at ?? null,
    widgetSeenDomain: r.widget_seen_domain ?? null,
    prechatEnabled: r.prechat_enabled === true,
    prechatConfig: toPrechatConfig(r.prechat_config),
    followUpEmailEnabled: r.follow_up_email_enabled === true,
    telegramConnected: r.telegram_connected === true,
  };
}

/** ANX-065: подключить Telegram-бота к сайту (admin). Сервер валидирует токен
 *  через setWebhook и сохраняет его. */
export async function connectTelegram(
  client: ApiClient,
  siteId: string,
  botToken: string,
): Promise<{ telegramConnected: boolean }> {
  const raw = await client.request<{ telegram_connected: boolean }>(
    `/api/v1/sites/${siteId}/telegram`,
    { method: 'POST', json: { bot_token: botToken } },
  );
  return { telegramConnected: raw.telegram_connected };
}

/** ANX-065: отключить Telegram-бота от сайта (снимает webhook + токен). */
export async function disconnectTelegram(
  client: ApiClient,
  siteId: string,
): Promise<{ telegramConnected: boolean }> {
  const raw = await client.request<{ telegram_connected: boolean }>(
    `/api/v1/sites/${siteId}/telegram`,
    { method: 'DELETE' },
  );
  return { telegramConnected: raw.telegram_connected };
}

export async function listSites(client: ApiClient): Promise<Site[]> {
  const raw = await client.request<{ sites: SiteRaw[] }>('/api/v1/sites');
  return raw.sites.map(toSite);
}

export interface CreateSiteInput {
  name: string;
  domain?: string;
}

export async function createSite(
  client: ApiClient,
  input: CreateSiteInput,
): Promise<Site> {
  const json: Record<string, unknown> = { name: input.name };
  if (input.domain !== undefined) json.domain = input.domain;
  const raw = await client.request<SiteRaw>('/api/v1/sites', {
    method: 'POST',
    json,
  });
  return toSite(raw);
}

export interface UpdateSiteInput {
  name?: string;
  domain?: string | null;
  status?: 'active' | 'paused';
  widgetSettings?: Partial<WidgetTheme>;
  /** ANX-050: toggle отправки email-транскриптов visitor'у после close. */
  emailTranscriptsEnabled?: boolean;
  /** ANX-051: pro-active trigger конфиг. */
  proactiveEnabled?: boolean;
  proactiveDelaySec?: number;
  proactiveMessage?: string;
  proactiveMobileEnabled?: boolean;
  /** ANX-061: часы работы. */
  officeHoursEnabled?: boolean;
  officeTimezone?: string;
  officeHours?: Record<string, OfficeDay>;
  offlineMessage?: string;
  offlineCollectEmail?: boolean;
  /** ANX-072: pre-chat форма. */
  prechatEnabled?: boolean;
  prechatConfig?: PrechatConfig;
  /** ANX-079: follow-up email. */
  followUpEmailEnabled?: boolean;
}

export async function updateSite(
  client: ApiClient,
  id: string,
  input: UpdateSiteInput,
): Promise<Site> {
  const json: Record<string, unknown> = {};
  if (input.name !== undefined) json.name = input.name;
  if (input.domain !== undefined) json.domain = input.domain;
  if (input.status !== undefined) json.status = input.status;
  if (input.widgetSettings !== undefined) json.widget_settings = input.widgetSettings;
  if (input.emailTranscriptsEnabled !== undefined) {
    json.email_transcripts_enabled = input.emailTranscriptsEnabled;
  }
  if (input.proactiveEnabled !== undefined) {
    json.proactive_enabled = input.proactiveEnabled;
  }
  if (input.proactiveDelaySec !== undefined) {
    json.proactive_delay_sec = input.proactiveDelaySec;
  }
  if (input.proactiveMessage !== undefined) {
    json.proactive_message = input.proactiveMessage;
  }
  if (input.proactiveMobileEnabled !== undefined) {
    json.proactive_mobile_enabled = input.proactiveMobileEnabled;
  }
  if (input.officeHoursEnabled !== undefined) {
    json.office_hours_enabled = input.officeHoursEnabled;
  }
  if (input.officeTimezone !== undefined) {
    json.office_timezone = input.officeTimezone;
  }
  if (input.officeHours !== undefined) {
    json.office_hours = input.officeHours;
  }
  if (input.offlineMessage !== undefined) {
    json.offline_message = input.offlineMessage;
  }
  if (input.offlineCollectEmail !== undefined) {
    json.offline_collect_email = input.offlineCollectEmail;
  }
  if (input.prechatEnabled !== undefined) {
    json.prechat_enabled = input.prechatEnabled;
  }
  if (input.prechatConfig !== undefined) {
    json.prechat_config = input.prechatConfig;
  }
  if (input.followUpEmailEnabled !== undefined) {
    json.follow_up_email_enabled = input.followUpEmailEnabled;
  }
  const raw = await client.request<SiteRaw>(`/api/v1/sites/${id}`, {
    method: 'PATCH',
    json,
  });
  return toSite(raw);
}

export async function deleteSite(client: ApiClient, id: string): Promise<void> {
  await client.request<void>(`/api/v1/sites/${id}`, { method: 'DELETE' });
}
